import React, { Component } from 'react';
import { components } from 'react-select';
import Icon from '../Icon';
import styled from 'styled-components';
import Tooltip from '../Tooltip';
import Creatable from 'react-select/lib/Creatable';

const DropdownIndicatorContainer = styled.div`
  margin-left: 4px;

  .selectIcon {
    font-size: ${(props) => (props.fontSize ? props.fontSize : '10px')};
    margin-right: ${(props) => (props.marginRight ? props.marginRight : '0')};
    color: ${(props) =>
      props.colorIcon ? props.colorIcon : props.theme.colors['terra-cotta']};
    background-color: ${(props) =>
      props.backgroundColor ? props.backgroundColor : 'transparent'};
    padding: 4px 4.5px;
    border-radius: 50%;
  }
  ${(props) => props.styles}
`;

const ClearIndicatorContainer = styled.div`
  .selectIcon {
    color: ${(props) =>
      props.colorIcon ? props.colorIcon : props.theme.colors['terra-cotta']};
    height: 15px;
    width: 15px;
  }
  ${(props) => props.styles}
`;

const SelectContainer = styled.div`
  min-width: ${(props) => (props.minWidth ? props.minWidth : '130px')};
  min-height: ${(props) => (props.minHeight ? props.minHeight : '')};
  display: ${(props) => (props.display ? props.display : 'block')};
  align-items: ${(props) => (props.alignItems ? props.alignItems : 'none')};
  justify-content: ${(props) =>
    props.justifyContent ? props.justifyContent : 'none'};
  position: 'absolute';
  font-family: ${(props) => props.theme.fonts.sfcompact_text};

  .label {
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.oswald};
    text-transform: uppercase;
    color: ${(props) => props.theme.colors.dark};
    margin-bottom: 7px;
  }

  .labelContainer {
    display: flex;
    flex-direction: row;
  }

  .labelContainer > div {
    margin-left: 4px;
  }

  .tooltipContainer {
    width: 12px;
  }

  ${(props) => props.containerStyles}
`;

export default class CustomSelect extends Component {
  constructor(props) {
    super(props);
    this.memoizedStyles = null;
    this.prevCustomStyles = null;
  }

  componentDidUpdate(prevProps) {
    // Clear memoized styles if customStyles prop changed
    if (prevProps.customStyles !== this.props.customStyles) {
      this.memoizedStyles = null;
      this.prevCustomStyles = null;
    }
  }

  DropdownIndicator = (props) =>
    components.DropdownIndicator && (
      <DropdownIndicatorContainer {...this.props.dropdownProps}>
        <components.DropdownIndicator {...props}>
          {this.props.dropdownIcon ? (
            <Icon icon={this.props.dropdownIcon} className="selectIcon" />
          ) : (
            <Icon icon={'dropdown'} className="selectIcon" />
          )}
        </components.DropdownIndicator>
      </DropdownIndicatorContainer>
    );

  ClearIndicator = (props) =>
    components.ClearIndicator && (
      <ClearIndicatorContainer {...this.props.clearProps}>
        <components.ClearIndicator {...props}>
          <Icon icon={'close'} className="selectIcon" />
        </components.ClearIndicator>
      </ClearIndicatorContainer>
    );

  IndicatorSeparator = (props) => {
    return (
      <div>
        <components.IndicatorSeparator {...props} style={{ display: 'none' }} />
      </div>
    );
  };

  getCustomStyles = () => {
    // Memoize styles to prevent infinite re-renders
    if (
      this.memoizedStyles &&
      this.prevCustomStyles === this.props.customStyles
    ) {
      return this.memoizedStyles;
    }

    this.prevCustomStyles = this.props.customStyles;
    this.memoizedStyles = this.customStyles();
    return this.memoizedStyles;
  };

  customStyles = () => {
    let { customStyles } = this.props;
    if (!customStyles) customStyles = {};
    return {
      container: () => ({
        borderColor: '#e8eaeb',
        outline: 'none !important',
        fontFamily: "'SFCompactText',sans-serif",
        fontSize: 12,
        position: 'relative',
        height: '45px',
        ...customStyles.container
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: 0,
        margin: 0
      }),
      menu: (base) => ({
        ...base,
        zIndex: 1000,
        width: '100%',
        minWidth: 150,
        borderRadius: 0,
        boxShadow: 'none',
        marginTop: 0,
        top: 'none',
        ...customStyles.menu
      }),
      groupHeading: (base) => ({
        ...base,
        ...customStyles.groupHeading
      }),
      menuList: (base) => ({
        ...base,
        padding: 0,
        '&:hover': { backgroundColor: 'white' },
        ...customStyles.menuList
      }),
      option: (base, { isDisabled }) => ({
        ...base,
        '&:hover': {
          backgroundColor: '#f7f7f7',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          fontWeight: isDisabled ? 'default' : 'bold',
          fontFamily: 'SFCompactText'
        },
        backgroundColor: 'white',
        cursor: isDisabled ? 'not-allowed' : 'default',
        color: isDisabled ? '#2b3d5d8a !important' : '#2B3D54',
        fontStyle: isDisabled ? 'italic !important' : 'normal',
        '&:not(:first-child)': { borderTop: '0.1px solid #f4f5f5' },
        borderRadius: 'none',
        fontFamily: 'SFCompactText',
        fontSize: 12,
        marginTop: -1,

        ...customStyles.option
      }),
      control: (base, { isDisabled }) => ({
        ...base,
        '&:hover': {
          borderColor: '#e8eaeb',
          cursor: isDisabled ? 'not-allowed' : 'pointer'
        },
        border: '1px solid #e8eaeb',
        outline: '0',
        borderRadius: 2,
        boxShadow: 'none',
        backgroundColor: 'white',
        cursor: isDisabled ? 'not-allowed' : 'default',

        height: '45px',
        ...customStyles.control
      }),
      placeholder: (base) => ({
        ...base,
        opacity: 0.7,
        fontFamily: 'SFCompactText',
        fontSize: '12px',
        fontWeight: 'normal',
        fontStyle: 'italic',
        fontStretch: 'normal',
        lineHeight: 'normal',
        letterSpacing: '-0.6px',
        color: '#1f2b37',
        ...customStyles.placeholder
      }),
      valueContainer: (base) => {
        return {
          ...base,
          maxWidth: 'calc(100% - 40px)',
          height: 45,
          position: 'initial',
          ...customStyles.valueContainer
        };
      },
      singleValue: (base) => {
        return {
          ...base,
          maxWidth: '90%',
          ...customStyles.singleValue
        };
      },
      multiValue: (styles) => {
        return {
          ...styles,
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          ...customStyles.multiValue
        };
      },
      multiValueLabel: (styles) => ({
        ...styles,
        color: '#1f2b37',
        ...customStyles.multiValueLabel
      }),
      multiValueRemove: (styles) => ({
        ...styles,
        borderRadius: '50%',
        width: 14,
        fontSize: 6,
        height: 14,
        padding: 0,
        backgroundColor: 'rgba(31,43,55,0.1)',
        color: 'rgba(39,53,68,0.5)',
        ':hover': {
          backgroundColor: 'rgba(31,43,55,0.3)'
        },
        ...customStyles.multiValueRemove
      })
    };
  };

  render() {
    return (
      <SelectContainer
        minWidth={this.props.minWidth}
        minHeight={this.props.minHeight}
        display={this.props.display}
        alignItems={this.props.alignItems}
        justifyContent={this.props.justifyContent}
        style={this.props.style}
        containerStyles={this.props.containerStyles}
      >
        {this.props.label && (
          <div className="labelContainer">
            <p className="label">{this.props.label}</p>
            {this.props.tooltip && (
              <div className="tooltipContainer">
                <Tooltip {...this.props.tooltip} />
              </div>
            )}
          </div>
        )}
        <Creatable
          isMulti={this.props.isMulti}
          isValidNewOption={() => this.props.creatable}
          isOptionDisabled={(option) => {
            return option.disabled || false;
          }}
          styles={this.getCustomStyles()}
          placeholder={this.props.isDisabled ? '--' : ''}
          {...this.props}
          components={{
            DropdownIndicator: this.DropdownIndicator,
            IndicatorSeparator: this.IndicatorSeparator,
            ClearIndicator: this.ClearIndicator,
            ...this.props.components
          }}
        />
      </SelectContainer>
    );
  }
}
